import React, { ComponentType, useEffect, useMemo, useState } from 'react';
import { RibbonProps, Type } from 'domain/constants/ribbons';
import { Container } from '@uds/grid';
import useGetSuggestions from 'domain/selectors/dst/useGetSuggestions';
import { useAppConfig, useUrls } from '@ux/whitelabel';
import useCheckDomainAvailability, {
  useCheckMultiDomainAvailability,
} from 'presentation/hooks/useCheckDomainAvailability';
import useAddDomainToBasket from 'application/actions/domain-search/useAddDomainToBasket';
import { DSTApiResponseExactDomain } from 'presentation/utils/dstApiResponseTypes';
import { Maybe } from 'core';
import { useBasket } from '@uds/bookends';
import { useDSTActionContext, useDSTContext } from 'presentation/components/pages/CMPage/DSTContext';
import useShowToast from 'application/actions/reactive-knowledge/useShowToast';
import FadeInOut from 'presentation/components/common/FadeInOut';
import { dispatchGAEvent, link } from '@uds/tagmg';
import { useCurrency } from '@ux/currency';
import { KnowledgeType } from '@uds/reactive-knowledge';
import { AddDomainToBasketType } from 'domain/constants/basket';
import { dispatchGAEventSearchMatchDomainName, dispatchGAEventDomainRanking } from 'presentation/utils/domainNameUtils';
import checkTldFromDomainQuery from 'domain/selectors/dst/checkTldFromDomainQuery';
import errorBoundary from 'presentation/hocs/errorBoundary';
import visibilitySet from 'presentation/hocs/visibilitySet';

import Ribbon from '../Ribbon';
import {
  AvailabilityStatus,
  getDomainStatus,
  handleAddDomainToBasket,
  handleAddMultiDomainToBasket,
  handleAddTransferDomainToBasket,
} from './utils';
import DomainSearchHero, { DomainSearchHeroActions, DomainSearchHeroText, HeroDesignOptions } from './DomainSearchHero';

export interface OuterProps extends RibbonProps {
  text: DomainSearchHeroText;
  design: HeroDesignOptions;
}

const ConnectedDomainSearchHero = ({ id, name, colorway, design, text, layout, loading }: OuterProps) => {
  const { data, isLoading, isError } = useGetSuggestions();
  const { bundle, transfer, dbsStatus: dbsFeature, priceInterval } = useAppConfig('dst');
  const { basket: basketURL } = useUrls();
  const { data: basket } = useBasket();
  const query = useDSTContext();
  const showToast = useShowToast();
  const setQuery = useDSTActionContext();

  const { domainName, exactMatch } = query;
  const exactMatchProps: Maybe<DSTApiResponseExactDomain> = data?.body?.exactDomain;

  const bundleDomains = useMemo(() => exactMatchProps?.bundles ?? [], [exactMatchProps]);
  const bundleDomainNames: Array<string> = bundleDomains?.map((bd) => bd.fqdn) ?? [];

  const { mutateAsync: addToBasket } = useAddDomainToBasket({
    onMutate: () => {
      setQuery({ type: 'DISABLE_CTA_BUTTONS' });
    },
    onError: () => {
      showToast('usf.addToBasket.failure', KnowledgeType.ERK);
    },
    onSettled: (_, __, { isLastBundleItem, fromBundle }) => {
      if (fromBundle !== true) {
        setQuery({ type: 'ENABLE_CTA_BUTTONS' });
      } else if (isLastBundleItem === true) {
        setQuery({ type: 'ENABLE_CTA_BUTTONS' });
      }
    },
    onSuccess: (_, { isLastBundleItem, fromBundle }) => {
      if (fromBundle !== true || isLastBundleItem === true) {
        showToast('usf.addToBasket.success');
      }
    },
  });

  const fqdn = exactMatchProps?.fqdn;
  const fqdnType = exactMatchProps?.type;
  const extension = exactMatchProps?.extension;
  const domainListToAdd = fqdn != null ? [fqdn, ...bundleDomainNames] : bundleDomainNames;
  const domainListToAddNotInBasket = domainListToAdd.filter((domain) => {
    const isDomainInBasket = basket?.items != null && basket.items.some((item) => item.domainName === domain);
    return !isDomainInBasket;
  });

  const addBundleToBasket = async (type: AddDomainToBasketType = AddDomainToBasketType.DOMAINS) => {
    const responses = [];
    const bundleDomainNamesExtensions: Array<string> = bundleDomains?.map((bd) => bd.extension) ?? [];
    const extensionsListToAdd =
      extension != null ? [extension, ...bundleDomainNamesExtensions] : bundleDomainNamesExtensions;

    const bundleDomainNamesDomainType: Array<string> = bundleDomains?.map((bd) => bd.type) ?? [];
    const fqdnTypesListToAdd =
      fqdnType != null ? [fqdnType, ...bundleDomainNamesDomainType] : bundleDomainNamesDomainType;
    const domainListToAddWithTld: Array<{ domain: string; tld: string; fqdnType: string }> =
      domainListToAddNotInBasket.map((value, index) => ({
        domain: value,
        tld: extensionsListToAdd[index],
        fqdnType: fqdnTypesListToAdd[index],
      }));

    for (let i = 0; i < domainListToAddWithTld.length; i++) {
      responses.push(
        await addToBasket({
          fqdn: domainListToAddWithTld[i].domain,
          fqdnType: domainListToAddWithTld[i].fqdnType,
          type: type,
          fromBundle: true,
          isLastBundleItem: i === domainListToAddWithTld.length - 1,
        }),
      );
    }
    return responses;
  };

  const { refetch: checkAvailability } = useCheckDomainAvailability(fqdn);

  const { refetch: checkBundleAvailability } = useCheckMultiDomainAvailability(domainListToAddNotInBasket);

  const [availability, setAvailability] = useState(AvailabilityStatus.UNVERIFIED);
  const [bundleAvailability, setBundleAvailability] = useState(AvailabilityStatus.UNVERIFIED);
  const [transferAvailability, setTransferAvailability] = useState(AvailabilityStatus.UNVERIFIED);

  /**
   * When query changes we need to check again the availability from basket however we
   * should not use the previous status but the unverified as the previous status is from the last query.
   */
  useEffect(() => {
    setAvailability(AvailabilityStatus.UNVERIFIED);
    setBundleAvailability(AvailabilityStatus.UNVERIFIED);
    setTransferAvailability(AvailabilityStatus.UNVERIFIED);
  }, [domainName, exactMatch, setAvailability, setBundleAvailability, setTransferAvailability]);

  const currency = useCurrency();

  /**
   * TODO: Double check this requirement.
   * What should we display when the response returns an error.
   *
   */
  if (data == null || isError || exactMatchProps == null || fqdn == null || isLoading || currency == null) {
    return null;
  }

  const status = getDomainStatus(exactMatchProps);
  const price = exactMatchProps.price?.display;
  const oldPrice = exactMatchProps.oldPrice?.display;

  const isPremium =
    exactMatchProps.inventory === 'premium' ||
    exactMatchProps.availability_label === 'premium' ||
    exactMatchProps.is_registry_premium ||
    ['registry_premium', 'auction'].includes(exactMatchProps.availability_label);

  /**
   *  TODO: Double check this feature as
   *  we don't see is_ad in DST response for exactMatch.
   *
   */
  const isAd = exactMatchProps.is_ad;

  const features = {
    bundle,
    dbs: dbsFeature,
    transfer,
  };

  const dbsStatusExactDomain = exactMatchProps.is_dbs_available;

  /**
   *  TODO: This property in the old DST was derived from prop 'transferableTlds'
   *  which came from the old CMS DST ribbon.
   *  There are some configs in brand pilot over DST key, however does not seem
   *  to be used for that.
   *
   *  TLD(Top level domain) eg: .com .co.uk, etc
   */
  const canTransfer = features.transfer; /* TODO: && current tld is a transferable tld */

  const legalMessage = exactMatchProps.legalText;

  const dispatchGAEventDomainExtension = () => {
    const tealiumPayload: Record<string, string> = {};
    tealiumPayload['action'] = 'click';
    tealiumPayload['category'] = 'Basket';
    tealiumPayload['label'] = 'Domain Extension';

    tealiumPayload['domain_extension'] = `.${extension}`;
    link(tealiumPayload);
  };

  const dispatchGAEventDomainExtensionSearched = () => {
    const tealiumPayloadDomainExtensionSearched: Record<string, string | null> = {};
    tealiumPayloadDomainExtensionSearched['action'] = 'click';
    tealiumPayloadDomainExtensionSearched['category'] = 'Basket';
    tealiumPayloadDomainExtensionSearched['label'] = 'Domain Extension Searched';

    tealiumPayloadDomainExtensionSearched['domain_extension_searched'] = checkTldFromDomainQuery(domainName);
    link(tealiumPayloadDomainExtensionSearched);
  };

  const actions: DomainSearchHeroActions = {
    handleAddDomainToBasket: () => {
      dispatchGAEvent({
        hitType: 'event',
        eventCategory: 'Basket',
        eventAction: 'click',
        eventLabel: text.domain.CTA.analytics ?? 'dst-v2-exact-domain-add-to-basket',
      });
      dispatchGAEventDomainExtension();
      dispatchGAEventDomainExtensionSearched();

      dispatchGAEventSearchMatchDomainName(exactMatchProps.name_without_extension, exactMatchProps.search_phrase);
      dispatchGAEventDomainRanking(1, true);

      handleAddDomainToBasket({
        setAvailability,
        // @ts-ignore: error after upgrading to react-query v3
        checkAvailability,
        addToBasket: () =>
          addToBasket({
            fqdn: fqdn,
            fqdnType: fqdnType,
            type: AddDomainToBasketType.DOMAINS,
          }),
        showToast,
      });
    },
    handleAddBundleToBasket: () => {
      dispatchGAEvent({
        hitType: 'event',
        eventCategory: 'Basket',
        eventAction: 'bundle',
        eventLabel: text.bundle.CTA.analytics ?? 'dst-v2-bundle-add-to-basket',
      });

      dispatchGAEventDomainRanking(1, true);

      handleAddMultiDomainToBasket({
        addToBasket: addBundleToBasket,
        setAvailability: setBundleAvailability,
        // @ts-ignore: error after upgrading to react-query v3
        checkAvailability: checkBundleAvailability,
        showToast,
      });
    },
    handleViewBasket: () => {
      // Analytics events are placed inside respective checkout buttons
      window.location.href = basketURL;
    },
    handleAddTransferDomainToBasket: () => {
      dispatchGAEvent({
        hitType: 'event',
        eventCategory: 'Basket',
        eventAction: 'transfer',
        eventLabel: text.transfer.CTA.analytics ?? 'dst-v2-transfer-add-to-basket',
      });
      handleAddTransferDomainToBasket({
        addToBasket: () =>
          addToBasket({
            fqdn: fqdn,
            fqdnType: fqdnType,
            type: AddDomainToBasketType.TRANSFERS,
          }),
        setAvailability: setTransferAvailability,
      });
    },
  };

  const getDomainAvailabilityFromBasket = (): AvailabilityStatus => {
    const isAddedToBasket = basket?.items?.some((product) => product.domainName === fqdn);

    if (isAddedToBasket && fqdn != null && query != null) {
      return AvailabilityStatus.IN_BASKET;
    }
    return availability;
  };

  const getBundleAvailabilityFromBasket = (): AvailabilityStatus => {
    if (domainListToAdd.length === 0 && query != null && fqdn != null) {
      return bundleAvailability;
    }
    const isBundleAddedToBasket = domainListToAdd.every((domain) => {
      return basket?.items?.some((product) => product.domainName === domain);
    });

    if (isBundleAddedToBasket) {
      return AvailabilityStatus.IN_BASKET;
    }
    return bundleAvailability;
  };

  const getTransferAvailabilityFromBasket = (): AvailabilityStatus => {
    const isTransferAddedToBasket = basket?.items?.some(
      (product) => product.domainName === fqdn && /\/transfers\//i.test(product.productType ?? ''),
    );

    if (isTransferAddedToBasket && query != null) {
      return AvailabilityStatus.IN_BASKET;
    }

    return transferAvailability;
  };

  /**
   * Checks if the availabilities are in basket and set the final availability
   * to IN_BASKET if they are added.
   */
  let finalAvailability = getDomainAvailabilityFromBasket();
  const finalBundleAvailability = getBundleAvailabilityFromBasket();
  const finalTransferAvailability = getTransferAvailabilityFromBasket();

  // When bundle is loading
  const isBundleLoading = [AvailabilityStatus.VERIFYING, AvailabilityStatus.AVAILABLE].includes(
    finalBundleAvailability,
  );

  if (isBundleLoading) {
    finalAvailability = AvailabilityStatus.VERIFYING;
  }

  return (
    <Ribbon
      id={id}
      name={name}
      type={Type.DOMAIN_SEARCH_RESULT_HERO}
      colorway={colorway}
      layout={layout}
      loading={loading}
      flush={true}
    >
      <Container>
        <FadeInOut transitionKey={fqdn}>
          <DomainSearchHero
            text={text}
            availability={finalAvailability}
            bundleAvailability={finalBundleAvailability}
            fqdn={fqdn}
            pricingStrategy={priceInterval}
            fqdnExtension={exactMatchProps.extension}
            currency={currency}
            price={price}
            oldPrice={oldPrice}
            actions={actions}
            isPremium={isPremium}
            isAd={isAd}
            bundleDomains={bundleDomains}
            design={design}
            status={status}
            legalMessage={legalMessage}
            features={features}
            dbsStatus={dbsStatusExactDomain}
            canTransfer={canTransfer}
            transferAvailability={finalTransferAvailability}
          />
        </FadeInOut>
      </Container>
    </Ribbon>
  );
};

ConnectedDomainSearchHero.displayName = 'DomainSearchHero';

export default errorBoundary(visibilitySet(ConnectedDomainSearchHero)) as ComponentType<OuterProps>;
